import React from 'react';
import { Player } from '../../../types';

interface PlayerAvatarProps {
  player: Player;
  isMobile: boolean;
  isCompact: boolean;
  showCreatorBadge: boolean;
  isMVP: boolean;
  isTopScorer: boolean;
  isTopAssister: boolean;
  isTopSaver: boolean;
}

const getPositionColor = (position: string) => {
  switch (position) {
    case 'Arquero':
      return '#FFD600';
    case 'Defensor':
      return '#2196F3';
    case 'Mediocampista':
      return '#4CAF50';
    case 'Delantero':
      return '#FF5722';
    default:
      return '#9E9E9E';
  }
};

const getPositionEmoji = (position: string) => {
  switch (position) {
    case 'Arquero':
      return '🥅';
    case 'Defensor':
      return '🛡️';
    case 'Mediocampista':
      return '⚽';
    case 'Delantero':
      return '🚀';
    default:
      return '👤';
  }
};

export const PlayerAvatar: React.FC<PlayerAvatarProps> = ({
  player,
  isMobile,
  isCompact,
  showCreatorBadge,
  isMVP,
  isTopScorer,
  isTopAssister,
  isTopSaver
}) => {
  const isDesktop = !isMobile && !isCompact;
  const avatarSize = isDesktop ? 26 : (isMobile || isCompact ? 28 : 32);

  // Determine badge emoji (priority order)
  let badgeEmoji = '';
  if (showCreatorBadge) badgeEmoji = '🏆';
  else if (isMVP) badgeEmoji = '👑';
  else if (isTopScorer) badgeEmoji = '⚽';
  else if (isTopAssister) badgeEmoji = '➡️';
  else if (isTopSaver) badgeEmoji = '🧤';

  return (
    <div style={{ position: 'relative' }}>
      <div style={{
        backgroundColor: getPositionColor(player.position),
        color: 'white',
        borderRadius: '50%',
        width: `${avatarSize}px`,
        height: `${avatarSize}px`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: isDesktop ? '12px' : (isMobile || isCompact ? '12px' : '14px'),
        flexShrink: 0,
        position: 'relative',
        zIndex: 1,
        overflow: 'hidden',
        border: '2px solid rgba(255, 255, 255, 0.2)'
      }}>
        {player.user?.avatar ? (
          <img
            src={player.user.avatar}
            alt={player.name}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              borderRadius: '50%'
            }}
            onError={(e) => {
              const target = e.currentTarget as HTMLImageElement;
              target.style.display = 'none';
              const fallback = target.parentElement?.querySelector('.position-fallback') as HTMLElement;
              if (fallback) {
                fallback.style.display = 'flex';
              }
            }}
          />
        ) : null}
        <div
          className="position-fallback"
          style={{
            display: player.user?.avatar ? 'none' : 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%'
          }}
        >
          {getPositionEmoji(player.position)}
        </div>
      </div>
      {/* Badge as corner overlay */}
      {badgeEmoji && (
        <span style={{
          position: 'absolute',
          bottom: isDesktop ? -3 : -6,
          right: isDesktop ? -3 : -6,
          fontSize: isDesktop ? '11px' : '14px',
          zIndex: 2,
          background: '#23272A',
          borderRadius: '50%',
          width: isDesktop ? '16px' : '20px',
          height: isDesktop ? '16px' : '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px solid rgba(255,255,255,0.2)',
          pointerEvents: 'none',
          lineHeight: 1,
        }}>{badgeEmoji}</span>
      )}
    </div>
  );
};
